import { Input } from '@aigc/antd';
import { AliOssImage, NonEmpty } from '@aigc/components';
import { usePagingQueryHelper } from '@aigc/hooks';
import { UserOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Alert,
  Button,
  Form,
  Grid,
  Image,
  Skeleton,
  Table,
  Tag,
  type TableColumnsType,
} from 'antd';
import { formatDuration, intervalToDuration } from 'date-fns';
import { zhCN } from 'date-fns/locale/zh-CN';
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type Ref,
} from 'react';
import { useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import { object, string, type InferType } from 'yup';
import { FormControl } from '../../../components/form-control';
import fallbackImage from '../../common/fallback.png';
import { ImageCredit } from '../../credit/components/image-credit';
import {
  getBaseTaskListQueryKey,
  getImageTranslationTaskListQueryOptions,
} from '../../task/api';
import { TaskStatus } from '../../task/components/task-status';
import {
  TaskType,
  type BatchImageTranslationTaskDto,
  type ImageTranslationTaskDto,
} from '../../task/model';
import { getOneVideoGenerationTaskStatusQueryOptions } from '../../video/api';
import { getImageModelName } from '../image-llm';

const querySchema = object({
  name: string().nullable(),
});

type QueryFormValues = InferType<typeof querySchema>;

export const TranslateImageList = (props: {
  ref?: Ref<{ refresh: () => void }>;
  start_time?: number;
  end_time?: number;
  admin?: boolean;
  onEdit?: (
    task: BatchImageTranslationTaskDto | ImageTranslationTaskDto,
  ) => void;
}) => {
  const screen = Grid.useBreakpoint();

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(querySchema),
  });

  const { pagination, queryMany, queryParams, changePageNumber } =
    usePagingQueryHelper<QueryFormValues>({
      defaultPagination: { page: 1, size: 10 },
      defaultQueryParams: {
        name: '',
      },
      onRefresh: () => {
        refetch();
      },
    });

  const {
    data: history,
    isFetching,
    refetch,
  } = useQuery({
    ...getImageTranslationTaskListQueryOptions({
      ...pagination,
      name: queryParams?.name ?? void 0,
    }),
    placeholderData: keepPreviousData,
  });

  useImperativeHandle(props.ref, () => ({
    refresh: () => refetch(),
  }));

  const columns = useMemo(() => {
    const _columns: TableColumnsType<
      ImageTranslationTaskDto | BatchImageTranslationTaskDto
    > = [
      {
        className: '!pr-0',
        width: 144,
        align: 'left',
        render: (_, row) => {
          if (row.type === TaskType.图片翻译) {
            if (row.status !== 'succeeded' && row.status !== 'failed') {
              return (
                <Skeleton.Image
                  classNames={{ content: '!w-20 !h-20' }}
                  active
                />
              );
            }

            if (row.status === 'failed') {
              return <Skeleton.Image classNames={{ content: '!w-20 !h-20' }} />;
            }

            return (
              <AliOssImage
                classNames={{ image: 'rounded' }}
                size={80}
                src={row.result?.image_url}
                fallback={fallbackImage}
              ></AliOssImage>
            );
          } else {
            return (
              <Image.PreviewGroup
                items={row.generate_data.images.map((item) => item.url)}
              >
                <AliOssImage
                  classNames={{ image: 'rounded' }}
                  size={80}
                  src={row.generate_data.images.at(0)?.url}
                  fallback={fallbackImage}
                ></AliOssImage>
              </Image.PreviewGroup>
            );
          }
        },
      },
      {
        render: (_, task) => {
          return (
            <ImageListItem
              task={task}
              dataUpdatedAt={task.updated_at}
            ></ImageListItem>
          );
        },
      },
      {
        width: 100,
        render: (_, task) => {
          return match(task)
            .with({ type: TaskType.批量图片翻译 }, (task) => {
              return (
                TaskStatus.isSucceeded(task.status) && (
                  <div className="flex flex-col gap-y-2">
                    <Button
                      variant="filled"
                      color="primary"
                      onClick={() => {
                        props.onEdit?.(task);
                      }}
                    >
                      重新翻译
                    </Button>
                    <Button
                      variant="filled"
                      color="primary"
                      onClick={() => {
                        const fragment = document.createDocumentFragment();
                        for (const item of task.children ?? []) {
                          if (!item.result?.image_url) {
                            continue;
                          }

                          const iframe = document.createElement('iframe');
                          iframe.hidden = true;
                          iframe.src = item.result.image_url;
                          iframe.addEventListener('', () => {
                            console.log('object');
                          });
                          fragment.appendChild(iframe);
                        }
                        document.body.appendChild(fragment);
                      }}
                    >
                      批量下载
                    </Button>
                  </div>
                )
              );
            })
            .otherwise((task) => (
              <Button
                variant="filled"
                color="primary"
                onClick={() => {
                  props.onEdit?.(task);
                }}
              >
                重新翻译
              </Button>
            ));
        },
      },
    ];

    return _columns;
  }, [props.admin]);

  const onQuery = handleSubmit((values) => {
    queryMany({
      name: values.name,
    });
  });

  return (
    <Table
      rowKey={(row) => row.id}
      showHeader={false}
      loading={isFetching}
      expandable={{
        indentSize: 0,
      }}
      dataSource={history?.items}
      columns={columns}
      title={() => (
        <Form layout="inline">
          <FormControl
            control={control}
            name="name"
            label="名称"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''}></Input>
            )}
          ></FormControl>
          <Button
            className="mr-2"
            onClick={onQuery}
            variant="solid"
            htmlType="submit"
            color="primary"
          >
            查询
          </Button>
          <Button
            variant="outlined"
            color="default"
            onClick={() => {
              reset();
              onQuery();
            }}
          >
            重置
          </Button>
        </Form>
      )}
      pagination={{
        total: history?.total,
        simple: screen.xs,
        current: pagination.page,
        pageSize: pagination.size,
        onChange: (page, pageSize) => {
          changePageNumber(page, pageSize);
        },
        showTotal: (total) => `共 ${total} 个`,
      }}
    ></Table>
  );
};

export const ImageListItem = (props: {
  task: ImageTranslationTaskDto | BatchImageTranslationTaskDto;
  dataUpdatedAt?: number;
  admin?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { task, dataUpdatedAt } = props;

  const [enabed, setEnabled] = useState(
    () => !TaskStatus.isSettled(task.status),
  );

  const { data } = useQuery({
    enabled: enabed,
    ...getOneVideoGenerationTaskStatusQueryOptions({
      id: task.id,
      model: task.model,
    }),
    initialData: task,
    initialDataUpdatedAt: dataUpdatedAt,
    staleTime: 5000,
    refetchInterval: 5000,
    structuralSharing: false,
  });

  const _data = data as ImageTranslationTaskDto | undefined;

  useEffect(() => {
    setEnabled(!TaskStatus.isSettled(_data?.status));

    if (task.status !== _data?.status) {
      queryClient.invalidateQueries({
        queryKey: getBaseTaskListQueryKey(),
      });
    }
  }, [_data, queryClient, task.status]);

  if (!_data) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="mb-2 flex flex-col items-start gap-2 sm:flex-row sm:items-center">
        <Tag># {_data.id}</Tag>
        <NonEmpty value={_data.generate_data.name} render={(v) => v}></NonEmpty>
      </div>
      <div className="space-x-2">
        <TaskStatus task={_data}></TaskStatus>
        {props.admin && _data.creator?.name && (
          <Tag icon={<UserOutlined />}>{_data.creator.name}</Tag>
        )}
        <ImageCredit task={_data}></ImageCredit>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <NonEmpty
          value={_data.model}
          render={(value) => (
            <span key="model">模型：{getImageModelName(value)}</span>
          )}
        ></NonEmpty>
        <NonEmpty
          value={_data.created_at}
          render={(value) => (
            <span key="created_at">
              创建时间： {new Date(value).toLocaleString()}
            </span>
          )}
        ></NonEmpty>
        {TaskStatus.isSettled(_data.status) && (
          <span className="text-xs">
            耗时：
            {formatDuration(
              intervalToDuration({
                start: _data.created_at,
                end: _data.updated_at ?? 0,
              }),
              { locale: zhCN },
            )}
          </span>
        )}
      </div>
      {_data.error && TaskStatus.isFailed(_data.status) && (
        <Alert
          classNames={{ title: 'break-words break-all' }}
          type="error"
          title={<span>{_data.error}</span>}
        ></Alert>
      )}
    </div>
  );
};
